import { useEffect, useMemo, useRef } from 'react';
import type { MatchEvent } from '../../engine';
import { t } from '../../i18n';
import { formatEvent, type Names } from '../../app/describe';

/**
 * Every event in notation (§19.12). New lines are announced politely to screen readers, unless
 * the page announces them itself (`announce` false: the match screen, where the log is optional).
 */
export function LogPanel({
  events,
  names,
  limit = 400,
  announce = true,
  heading = true,
}: {
  events: readonly MatchEvent[];
  names: Names;
  limit?: number;
  announce?: boolean;
  heading?: boolean;
}) {
  const lines = useMemo(() => {
    const out: { key: number; text: string; kind: string }[] = [];
    events.forEach((e, i) => {
      const text = formatEvent(e, names);
      if (text) out.push({ key: i, text, kind: e.type });
    });
    return out.slice(-limit);
  }, [events, names, limit]);
  // New lines keep the list scrolled to the end unless the reader has scrolled up. Only the list
  // scrolls: scrollIntoView would also scroll the page, pulling the board out of view on phones.
  const listRef = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const list = listRef.current;
    if (list && pinned.current) list.scrollTop = list.scrollHeight;
  }, [lines.length]);
  const latest = lines[lines.length - 1];
  return (
    <section className="log-panel" aria-label={t('match.log')}>
      {heading && <h3 className="mini-label">{t('match.log')}</h3>}
      <ol
        className="log-list"
        ref={listRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {lines.map((l) => (
          <li key={l.key} className={`log-${l.kind}`}>
            {l.text}
          </li>
        ))}
      </ol>
      {announce && (
        <p className="sr-only" aria-live="polite">
          {latest?.text}
        </p>
      )}
    </section>
  );
}
